/* global Sys, Core, EventHandler, Resources, Game, Services */
Sys.ns("Core");

/**
 * The State Handler manages states and state transitions in the game.
 *
 * @class Core.StateHandler
 * @extends Sys.Observable
 */
Core.StateHandler = {
    constructor: function() {
        var me = this;

        Core.StateHandler.superclass.constructor.apply(me, arguments);

        me.useLogging = false;

        me.states = window.GenericStates || {};
        me.stateStack = [];
        me.runningStateMachine = false;
    },

    /**
     * Registers one or several events as wait events.
     *
     * @param {string|string[]} events The event(s) to register.
     * @returns {void}
     */
    registerEvents: function(events) {
        var me = this,
            numEvents = events.length,
            i;

        if (Sys.isArray(events)) {
            for (i = -1; ++i < numEvents;) {
                me.addListener(events[i], me.onDefaultEventHandler.bind(me, events[i]));
            }
        }
        else if (typeof events === "string") {
            me.addListener(events, me.onDefaultEventHandler.bind(me, events));
        }
    },

    /**
     * Generic event handler for all events registered as wait events.
     * Dispatches the event to all states in the state stack and the current state and
     * then processes the states.
     *
     * @param {string} eventName Event name.
     * @returns {void}
     */
    onDefaultEventHandler: function(eventName) {
        this.dispatchEvent(eventName);
        this.activateStateMachine();
    },

    /**
     * Starts up the state handler.
     *
     * @returns {void}
     */
    initStateHandler: function() {
        var me = this,
            initResponse;

        // Add generic states to the state handler.
        Sys.iterate(window.GenericStates || {}, function(key, state) {
            me._registerTrigger(state);
        });

        // Sort the event listeners now when all modules has finished loading
        EventHandler.sortEventListeners(this);

        initResponse = Resources.readData("gameServerInitResponse");

        // Only push idle state if not a restore state.
        if (initResponse.restore !== true) {
            this.pushState(this.states.idle);
        }

        this.pushState(this.states.beforeLoaderClose);

        // State for modules that needs to process images, create object etc before the game begins.
        // Modules can push in their own states on this one, knowing that all image object are created and ready.
        this.pushState(this.states.setupGame);

        this.activateStateMachine();
    },

    /**
     * Makes the state handler process the current state and state stack.
     * If the current state is completed (has received all its wait events) the state stack will be processed.
     * If the current state is not completed the function will return.
     * The order of things happening when processing the state stack is:
     * 1. Firing of notify:stateHandler.leavingStateName State event.
     * 2. Setting the next state as the current state.
     * 3. Execution of the next state's execute function.
     * 4. Execution of any module hooks connected to this state (specified as a queue state change in the module file).
     * 5. Execution of the state's executeLast function if specified.
     * 6. Firing of notify:stateHandler.enteringStateName State event.
     * 7. Restart and check if the current state is completed.
     *
     * @returns {void}
     */
    activateStateMachine: function() {
        var me = this,
            prop;

        /*
         * To prevent recursive execution of this function we return if already running
         */
        if (me.runningStateMachine) {
            return;
        }

        me.runningStateMachine = true;

        while (me.hasStatesInStack()) {
            // Handle the current state.
            if (!Sys.isEmpty(me.currentState)) {
                /* DEBUG_START*/
                me.dumpStack("Activating, current state: " + me.currentState.name);

                /* DEBUG_END*/

                for (prop in me.currentState.waitEvents) {
                    // If we have not gotten all wait events we return and wait for the next event to come in
                    if (me.currentState.waitEvents.hasOwnProperty(prop) && !me.currentState.waitEvents[prop]) {
                        // Allow re-entry again.
                        me.runningStateMachine = false;
                        return;
                    }
                }

                // Otherwise we leave the current state.
                setTimeout(me.leaveCurrentState.bind(me), 1);
                return;
            }

            // Take the next state off the stack and set it as current.
            me.currentState = me.stateStack.pop();

            // Run the current state.
            me.currentState.execute({
                stateHandler: me,
                states: me.states
            });

            // Run any code hooked in by modules.
            me.processModuleQueue();

            // Run anything we want to run after all the modules.
            if (Sys.isDefined(me.currentState.executeLast)) {
                me.currentState.executeLast({
                    stateHandler: me,
                    states: me.states
                });
            }

            // Notify the rest of the game of the state change
            me.fireEvent("notify:stateHandler.entering" + me.currentState.name + "State");

            /* DEBUG_START*/
            me.dumpStack("Executed " + me.currentState.name);

            /* DEBUG_END*/
        }

        // Allow re-entry again.
        me.runningStateMachine = false;
    },

    /**
     * Takes care of leaving the current state.
     *
     * @private
     * @returns {void}
     */
    leaveCurrentState: function() {
        var me = this;

        me.fireEvent("notify:stateHandler.leaving" + me.currentState.name + "State");
        me.currentState = undefined;
        me.runningStateMachine = false;
        me.activateStateMachine();
    },

    /**
     * Execute all the module functions in the queue for the current state.
     *
     * @private
     * @returns {void}
     */
    processModuleQueue: function() {
        var me = this,
            queue = Game.moduleLoader.getModuleQueueForState(me.currentState.name),
            queueLength = queue.length,
            i;

        for (i = 0; i < queueLength; i++) {
            queue[i]({
                stateHandler: me,
                states: me.states
            });
        }
    },

    /**
     * Add a state to the states list if it does not already exist.
     *
     * @param {string} name The name of the state.
     * @param {Object} state The new state.
     * @returns {void}
     */
    addState: function(name, state) {
        if (!Sys.isDefined(this.states[name])) {
            this.states[name] = state;
            this._registerTrigger(state);
        }
    },

    /**
     * Registers the trigger events.
     *
     * @private
     * @param {Object} state The new state.
     * @returns {void}
     */
    _registerTrigger: function(state) {
        // Add triggers when entering and leaving every state.
        if (Sys.isDefined(Services.trigger)) {
            Services.trigger.registerTrigger("notify:stateHandler.entering" + state.name + "State", this, "When entering the state " + state.name);
            Services.trigger.registerTrigger("notify:stateHandler.leaving" + state.name + "State", this, "When leaving the state " + state.name);
        }
    },

    /**
     * Pushes a state to the top of the state stack.
     *
     * @param {Object} state A state object.
     * @param {string} state.name The name of the state. The first letter should be capitalized.
     * @param {Function} state.execute Function that is executed when the state becomes the current state.
     * @param {Object} state.waitEvents Events that needs to be fired before this state is considered complete. May be an empty object.
     * @param {Function} [state.executeLast] Function that is executed when the state becomes the current state after any module hooks.
     * @returns {void}
     */
    pushState: function(state) {
        var prop;

        if (this.stateStack.indexOf(state) > -1) {
            console.error("Trying to insert state '" + state.name + "', that is already in stack");
            return;
        }

        // Resetting event flags when adding state back on stack.
        for (prop in state.waitEvents) {
            if (state.waitEvents.hasOwnProperty(prop)) {
                state.waitEvents[prop] = false;
            }
        }
        this.stateStack.push(state);

        /* DEBUG_START*/
        this.dumpStack("Pushed " + state.name);

        /* DEBUG_END*/
    },

    /**
     * Marks the event as received for all states in the state stack and for the current state.
     *
     * @param {string} event The name of the fired event.
     * @returns {void}
     */
    dispatchEvent: function(event) {
        var me = this;

        /* DEBUG_START*/
        if (Sys.isEmpty(me.currentState)) {
            console.error("Illegal situation: stateHandler.currentState is undefined!");
        }

        me.dumpStack("Dispatching " + event);

        /* DEBUG_END*/

        if (!Sys.isEmpty(me.currentState) && !Sys.isEmpty(me.currentState.waitEvents[event])) {
            me.currentState.waitEvents[event] = true;
        }

        Sys.each(me.stateStack, function(state) {
            if (!Sys.isEmpty(state.waitEvents[event])) {
                state.waitEvents[event] = true;
            }
        });

        /* DEBUG_START*/
        me.dumpStack("Dispatched " + event);

        /* DEBUG_END*/
    },

    /**
     * Prints the message together with information regarding the current state and the state stack.
     *
     * @param {string} message The message.
     * @returns {void}
     */
    dumpStack: function(message) {
        var me = this,
            currWaitFlags = "",
            logMessage,
            stillWaiting,
            i;

        if (me.useLogging) {
            if (!this.currentState) {
                console.warn("No current state!");
            }
            else {
                logMessage = message;

                Sys.each(Object.getOwnPropertyNames(this.currentState.waitEvents), function(key) {
                    stillWaiting = me.currentState.waitEvents[key] ? "*" : " ";
                    currWaitFlags += "\n\t\t(" + stillWaiting + ") " + key.replace(/\s/g, "");
                });

                logMessage += "\nCurrent state:\t" + this.currentState.name + currWaitFlags;

                for (i = this.stateStack.length - 1; i >= 0; i--) {
                    logMessage += "\n\t" + this.stateStack[i].name;
                }

                console.info(logMessage);
            }
        }
    },

    /**
     * Method to check if there are any states added.
     *
     * @returns {boolean} True if states added, else false.
     */
    hasStates: function() {
        return Object.keys(this.states).length > 0;
    },

    /**
     * Method to check if there are states in stack.
     *
     * @returns {boolean} True if states in stack, else false.
     */
    hasStatesInStack: function() {
        return this.stateStack.length > 0;
    },

    /**
     * Method to check if state machine is running.
     *
     * @returns {boolean} True if running, else false.
     */
    isRunning: function() {
        return this.runningStateMachine;
    }
};

Core.StateHandler = Sys.extend(Sys.Observable, Core.StateHandler, "Core.StateHandler");
